import * as React from 'react';
import { useAuth } from './AuthProvider';
import { isRole, type Role } from '@/data/permissions';
import { toPolicy, type DispatchPolicy } from '@/lib/dispatchCheck';

/**
 * The caller's agency and role, read once under RequireAuth (design 2).
 *
 * Reads the caller's ACTIVE `agency_members` rows (the same `status = 'active'` rule `app.user_role`
 * applies, so the portal never shows a role the server does not honour), then those agencies. The
 * current agency is the one stored under `coreway.agency` while it is still a membership, else the
 * first by name. Every data hook filters by `agency.id`, so enabling the Topbar switcher later is UI.
 *
 * It always renders its children: `none`, `suspended` and `error` are for the layout to show (a
 * no-agency screen, an access-paused screen, a retry banner) while the shell and the sign-out stay
 * reachable.
 *
 * `suspended` (change spec operator-agency-onboarding): a suspended agency drops out of the office
 * helpers, so its members read zero memberships. Only then is `my_suspended_agencies()` asked (a
 * DEFINER read of the caller's own active memberships in suspended agencies: id, name, since when,
 * never the reason). Rows -> `suspended`; none, or a failed read -> `none`, exactly as before.
 */

export interface Agency {
  id: string;
  name: string;
  timeZone: string;
  suspendedCarriersCanCreateLoads: boolean;
  /** 0069: while a broker disputes a load, its agency fee stays off the carrier's weekly invoice. */
  brokerDisputeHoldsFee: boolean;
  /** 0069: days to pay of a broker without a credit check (a receivable's default due date). */
  brokerTermsDays: number;
  /** 0131 (broker credit): days a credit check stays valid, and whether a flagged broker warns or blocks. */
  brokerCreditExpiryDays: number;
  brokerCreditPolicy: 'warn' | 'block';
  /** 0300 (audit-a-dispatch): off | warn | block for a busy driver or truck, and for documents and broker setup. */
  dispatchConflictPolicy: DispatchPolicy;
  requireDocsOnDispatch: DispatchPolicy;
}

export interface Membership {
  agencyId: string;
  role: Role;
  agencyName: string;
  timeZone: string;
}

/** An agency the operator suspended, as its member sees it (0082 `my_suspended_agencies`). */
export interface SuspendedAgency {
  id: string;
  name: string;
  /** ISO timestamp. */
  suspendedAt: string;
}

export type AgencyState =
  | { status: 'loading' }
  | { status: 'suspended'; agencies: SuspendedAgency[]; reload(): void }
  | { status: 'error'; reload(): void }
  | { status: 'none'; reload(): void }
  | {
      status: 'ready';
      agency: Agency;
      role: Role;
      memberships: Membership[];
      setAgency(id: string): void;
      reload(): void;
    };

const STORAGE_KEY = 'coreway.agency';
/** Fallback zone when an agency row has none (the column is NOT NULL today; belt and braces). */
const DEFAULT_ZONE = 'America/Chicago';

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Store the agency the next AgencyProvider picks. The invite page calls it after an accept, before it
 * navigates into the layout (which mounts a fresh AgencyProvider that reads the new membership).
 */
export function writeStoredAgency(id: string): void {
  writeStored(id);
}

function writeStored(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // storage unavailable (private window): the choice lasts for this page only
  }
}

interface MemberRow {
  agency_id: string;
  role: string;
  status: string;
}

interface AgencyRow {
  id: string;
  name: string | null;
  time_zone: string | null;
  suspended_carriers_can_create_loads: boolean | null;
  broker_dispute_holds_fee: boolean | null;
  broker_terms_days: number | string | null;
  broker_credit_expiry_days?: number | string | null;
  broker_credit_policy?: string | null;
  dispatch_conflict_policy?: string | null;
  require_docs_on_dispatch?: string | null;
}

/** The column defaults of 0069, for a row that has none (never expected: both are NOT NULL). */
const DEFAULT_TERMS_DAYS = 30;
/** The column default of 0131. */
const DEFAULT_CREDIT_EXPIRY_DAYS = 30;

interface SuspendedRow {
  agency_id: string;
  agency_name: string | null;
  suspended_at: string;
}

type Loaded =
  | { status: 'loading' }
  | { status: 'suspended'; agencies: SuspendedAgency[] }
  | { status: 'error' }
  | { status: 'none' }
  | { status: 'ready'; agencies: Agency[]; memberships: Membership[] };

/**
 * The caller's suspended agencies. A failure is logged and reads as none: the screen then falls back
 * to the no-agency copy, never an error wall.
 */
async function readSuspended(client: ReturnType<typeof useAuth>['client']): Promise<SuspendedAgency[]> {
  const { data, error } = await client.rpc('my_suspended_agencies');
  if (error) {
    console.warn('AgencyProvider: could not read the suspended agencies', error);
    return [];
  }
  return ((data ?? []) as SuspendedRow[]).map((r) => ({
    id: r.agency_id,
    name: r.agency_name ?? '',
    suspendedAt: r.suspended_at,
  }));
}

const AgencyContext = React.createContext<AgencyState | null>(null);

/**
 * How often a tab on the paused screen asks again (F31). No realtime channel reaches a member of a
 * suspended agency (every office helper drops it), so the gate re-checks on a timer and whenever the tab
 * comes back to the foreground; the operator's reactivation then shows up without a reload.
 */
export const SUSPENDED_RECHECK_MS = 60_000;

export function AgencyProvider({ children }: { children: React.ReactNode }) {
  const { client, user } = useAuth();
  const userId = user?.id ?? null;
  const [loaded, setLoaded] = React.useState<Loaded>({ status: 'loading' });
  const [selected, setSelected] = React.useState<string | null>(() => readStored());
  const [nonce, setNonce] = React.useState(0);
  const reload = React.useCallback(() => setNonce((n) => n + 1), []);

  React.useEffect(() => {
    if (!userId) {
      setLoaded({ status: 'loading' });
      return;
    }
    let active = true;
    void (async () => {
      const members = await client
        .from('agency_members')
        .select('agency_id, role, status')
        .eq('user_id', userId)
        .eq('status', 'active');
      if (!active) return;
      if (members.error) {
        console.warn('AgencyProvider: could not read the memberships', members.error);
        setLoaded({ status: 'error' });
        return;
      }
      const rows = ((members.data ?? []) as MemberRow[]).filter((m) => isRole(m.role));
      if (rows.length === 0) {
        const suspended = await readSuspended(client);
        if (!active) return;
        setLoaded(suspended.length ? { status: 'suspended', agencies: suspended } : { status: 'none' });
        return;
      }
      const agenciesRes = await client
        .from('agencies')
        .select('id, name, time_zone, suspended_carriers_can_create_loads, broker_dispute_holds_fee, broker_terms_days, broker_credit_expiry_days, broker_credit_policy, dispatch_conflict_policy, require_docs_on_dispatch')
        .in(
          'id',
          rows.map((r) => r.agency_id),
        )
        .is('deleted_at', null);
      if (!active) return;
      if (agenciesRes.error) {
        console.warn('AgencyProvider: could not read the agencies', agenciesRes.error);
        setLoaded({ status: 'error' });
        return;
      }
      const agencies: Agency[] = ((agenciesRes.data ?? []) as AgencyRow[]).map((a) => ({
        id: a.id,
        name: a.name ?? '',
        timeZone: a.time_zone ?? DEFAULT_ZONE,
        suspendedCarriersCanCreateLoads: a.suspended_carriers_can_create_loads === true,
        brokerDisputeHoldsFee: a.broker_dispute_holds_fee !== false,
        brokerTermsDays: a.broker_terms_days == null ? DEFAULT_TERMS_DAYS : Number(a.broker_terms_days),
        brokerCreditExpiryDays: a.broker_credit_expiry_days == null ? DEFAULT_CREDIT_EXPIRY_DAYS : Number(a.broker_credit_expiry_days),
        brokerCreditPolicy: a.broker_credit_policy === 'block' ? 'block' : 'warn',
        dispatchConflictPolicy: toPolicy(a.dispatch_conflict_policy),
        requireDocsOnDispatch: toPolicy(a.require_docs_on_dispatch),
      }));
      const memberships: Membership[] = rows
        .map((r) => {
          const a = agencies.find((x) => x.id === r.agency_id);
          return a ? { agencyId: a.id, role: r.role as Role, agencyName: a.name, timeZone: a.timeZone } : null;
        })
        .filter((m): m is Membership => m !== null)
        .sort((x, y) => x.agencyName.localeCompare(y.agencyName));
      if (memberships.length === 0) {
        const suspended = await readSuspended(client);
        if (!active) return;
        setLoaded(suspended.length ? { status: 'suspended', agencies: suspended } : { status: 'none' });
        return;
      }
      setLoaded({ status: 'ready', agencies, memberships });
    })();
    return () => {
      active = false;
    };
  }, [client, userId, nonce]);

  // F31: while suspended, re-read on focus, on becoming visible, and every SUSPENDED_RECHECK_MS. A re-read
  // keeps the paused screen up until the answer changes (no loading flash in between).
  const paused = loaded.status === 'suspended';
  React.useEffect(() => {
    if (!paused) return;
    const onFocus = () => reload();
    const onVisible = () => {
      if (document.visibilityState === 'visible') reload();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(reload, SUSPENDED_RECHECK_MS);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [paused, reload]);

  const setAgency = React.useCallback((id: string) => {
    writeStored(id);
    setSelected(id);
  }, []);

  const value = React.useMemo<AgencyState>(() => {
    if (loaded.status === 'loading') return { status: 'loading' };
    if (loaded.status === 'error') return { status: 'error', reload };
    if (loaded.status === 'none') return { status: 'none', reload };
    if (loaded.status === 'suspended') return { status: 'suspended', agencies: loaded.agencies, reload };
    const current =
      loaded.memberships.find((m) => m.agencyId === selected) ?? loaded.memberships[0];
    const agency = loaded.agencies.find((a) => a.id === current.agencyId)!;
    return {
      status: 'ready',
      agency,
      role: current.role,
      memberships: loaded.memberships,
      setAgency,
      reload,
    };
  }, [loaded, selected, setAgency, reload]);

  return <AgencyContext.Provider value={value}>{children}</AgencyContext.Provider>;
}

export function useAgency(): AgencyState {
  const ctx = React.useContext(AgencyContext);
  if (!ctx) throw new Error('useAgency must be used inside AgencyProvider');
  return ctx;
}
